"use client";

import { useEffect, useMemo, useState } from "react";
import { ImageResponsiveProps } from "./Image.types";
import assetLoader from "@utils/internalImageLoder";
import Image from "next/image";
import {
  useScreenSizeDetection,
  useImageSizeSelection,
  useResponsiveProps,
  getImageStyle,
} from "./BaseImageResponsive";
import { useIsMobile } from "@hooks/use-mobile";

const ImageResponsive = ({
  filePath,
  imageTag,
  alt = "image",
  width,
  height,
  className = "",
  fill,
  priority = false,
  style,
  onClickHandler,
  containerClassName = "",
  responsiveWidth,
  fallBackImage
}: ImageResponsiveProps) => {
  const [useOriginal, setUseOriginal] = useState<boolean>(false);

  useEffect(()=> {
    setUseOriginal(false);
  },[filePath, imageTag]);

  const baseUrl = useMemo(
    () => `https://ackodrive-prod.ackoassets.com/image/${filePath}/images_cdn`,
    [filePath]
  );

  const originalSrc = useMemo(
    () => fallBackImage ? fallBackImage : `${assetLoader()}/images/placeHolder-FallbackImage.png`,
    []
  );

  const handleError = () => {
    console.log("error in url", baseUrl);
    setUseOriginal(true);
  };

  // Use common screen size detection
  const screenSize = useScreenSizeDetection();

  // Use common image size selection
  const effectiveWidth = responsiveWidth
    ? responsiveWidth
    : screenSize.viewportWidth > 0
    ? screenSize.viewportWidth
    : screenSize.width > 0
    ? screenSize.width
    : width;

  const { imageSrc } = useImageSizeSelection(
    screenSize,
    effectiveWidth,
    baseUrl,
    imageTag
  );

  // Use common responsive props
  const responsiveProps = useResponsiveProps(fill, width, height);

  // Use common image style
  const imageStyle = getImageStyle(fill, width, height, style);

  const finalClassName = useMemo(
    () => `${className} ${useOriginal ? "fallback-image" : ""}`,
    [className, useOriginal]
  );

  const defaultWidth = useIsMobile() ? 640 : 1280;

  const maxWidth = width || defaultWidth;

  return (
    <div onClick={onClickHandler} className={containerClassName}>
      <picture>
        {!useOriginal && (
          <source
            type="image/webp"
            srcSet={`
              ${baseUrl}/small/${imageTag}.webp 320w,
              ${baseUrl}/small/${imageTag}.webp 640w
              ${
                maxWidth >= 1024
                  ? `, ${baseUrl}/medium/${imageTag}.webp 1024w`
                  : ""
              }
              ${
                maxWidth >= 1280
                  ? `, ${baseUrl}/large/${imageTag}.webp 1280w`
                  : ""
              }
              ${
                maxWidth >= 1920
                  ? `, ${baseUrl}/xlarge/${imageTag}.webp 1920w`
                  : ""
              }
              ${
                maxWidth >= 2560
                  ? `, ${baseUrl}/original/${imageTag}.webp 2560w`
                  : ""
              }
            `}
            sizes={fill ? "100vw" : `${maxWidth}px`}
          />
        )}

        <Image
          src={useOriginal ? originalSrc : imageSrc}
          alt={alt}
          className={finalClassName}
          {...responsiveProps}
          style={imageStyle}
          loading={priority ? "eager" : "lazy"}
          onError={handleError}
          quality={90}
          fetchPriority={priority ? "high" : "auto"}
        />
      </picture>
    </div>
  );
};

export default ImageResponsive;
